import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { PublicHttpClientService } from "./http-client.service";

@Injectable()
export class PublicPersonsService {
  constructor(private publicHttp: PublicHttpClientService) {}

  public getPerson(id: string | number): Observable<any> {
    return this.publicHttp
      .get("/api/public/persons/" + id)
      .pipe(tap((_) => console.log("PersonsService.getPerson: Ok.")));
  }

  public getPersonBasicInfo(id: string | number): Observable<any> {
    return this.publicHttp
      .get("/api/public/persons/" + id + "/basic_info")
      .pipe(tap((_) => console.log("PersonsService.getPersonBasicInfo: Ok.")));
  }

  public getPersonAddresses(id: string | number): Observable<any> {
    return this.publicHttp
      .get("/api/public/persons/" + id + "/addresses")
      .pipe(tap((_) => console.log("PersonsService.getPersonAddresses: Ok.")));
  }

  public addPerson({
    first_name = undefined,
    middle_name = undefined,
    first_last_name = undefined,
    second_last_name = undefined,
    rfc = undefined,
    fecha_nacimiento = undefined,
    regime = undefined,
    phones = undefined,
    emails = undefined,
    client_id,
    relation = undefined,
    ingresos_comprobables = undefined,
    expenses = undefined,
    curp = undefined,
  }): Observable<any> {
    const params = {
      primer_nombre: first_name,
      segundo_nombre: middle_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      rfc: rfc,
      fecha_nacimiento: fecha_nacimiento,
      phones: phones,
      emails: emails,
      relation: relation,
      regime: regime,
      ingresos_comprobables: ingresos_comprobables,
      expenses: expenses,
      curp: curp,
    };
    return this.publicHttp
      .post("/api/public/contact/" + client_id + "/persons/create_person", params)
      .pipe(tap((_) => console.log("PersonsService.addPerson: Ok.")));
  }

  public checkViability(person_id) {
    return this.publicHttp
      .get(`/api/public/persons/${person_id}/leasings/viable`)
      .pipe(tap((_) => console.log("PersonsService.checkViability: Ok.")));
  }

  public updatePerson({
    first_name = undefined,
    middle_name = undefined,
    first_last_name = undefined,
    second_last_name = undefined,
    rfc = undefined,
    fecha_nacimiento = undefined,
    regime = undefined,
    phones = undefined,
    emails = undefined,
    client_id,
    relation = undefined,
    ingresos_comprobables = undefined,
    expenses = undefined,
    person_id,
    curp = undefined,
    banned = undefined,
  }): Observable<any> {
    const params = {
      primer_nombre: first_name,
      segundo_nombre: middle_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      rfc: rfc,
      fecha_nacimiento: fecha_nacimiento,
      phones: phones,
      emails: emails,
      relation: relation,
      regime: regime,
      ingresos_comprobables: ingresos_comprobables,
      expenses: expenses,
      person_id: person_id,
      curp: curp,
      banned,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] == undefined) {
        delete params[key];
      }
    });

    return this.publicHttp
      .post("/api/public/contact/" + client_id + "/persons/update_person", params)
      .pipe(tap((_) => console.log("PersonsService.updatePerson: Ok.")));
  }

  public updatePersonField(person_id, params): Observable<any> {
    return this.publicHttp
      .post("/api/public/contact/" + person_id + "/persons/update_person_contactable", params)
      .pipe(tap((_) => console.log("PersonsService.updatePersonField: Ok.")));
  }

  public duplicated({
    first_name,
    first_last_name = "",
    birthday,
    second_last_name = "",
    source_channel = undefined,
    prevent_change = false,
    id = undefined,
    curp = undefined,
    contact_id = undefined,
  }): Observable<any> {
    const body = {
      primer_nombre: first_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      fecha_nacimiento: birthday,
      source_channel: source_channel,
      prevent_change,
      id,
      curp,
      contact_id,
    };
    return this.publicHttp
      .post("/api/public/persons/duplicated", body)
      .pipe(tap((_) => console.log("PersonsService.duplicated")));
  }

  public getAddressValidationProcess(id): Observable<any> {
    return this.publicHttp
      .get("/api/public/validation_processes/" + id + "/address")
      .pipe(tap((_) => console.log("PersonsService.getAddressValidationProcess: Ok.")));
  }

  public createAddress(addressData, addressable_id, addressable_type, force_create: boolean = false): Observable<any> {
    const body = {
      street: addressData.street,
      interior_number: addressData.interiorNumber,
      exterior_number: addressData.exteriorNumber,
      postal_code: addressData.postalCode,
      sublocality: addressData.sublocality,
      municipality: addressData.municipality,
      state: addressData.state,
      longitud: addressData.longitud,
      latitud: addressData.latitud,
      addressable_id: addressable_id,
      addressable_type: addressable_type,
      force_create: force_create,
    };
    return this.publicHttp
      .post("/api/public/address", body)
      .pipe(tap((_) => console.log("PersonsService.createAddress: Ok.")));
  }

  public postRemindSelectQuoteEvent(id) {
    return this.publicHttp
      .post("/api/public/persons/" + id + "/remind_select_quote_event", null)
      .pipe(tap((_) => console.log("PersonsService.remindSelectQuoteEvent: Ok.")));
  }

  public postAddressValidationProcess(address_data, id): Observable<any> {
    const body = {
      street: address_data.street,
      interior_number: address_data.interior_number,
      exterior_number: address_data.exterior_number,
      postal_code: address_data.postal_code,
      sublocality: address_data.sublocality,
      municipality: address_data.municipality,
      state: address_data.state,
      longitud: address_data.longitud,
      latitud: address_data.latitud,
      complete_address: address_data.complete_address,
    };
    return this.publicHttp
      .post("/api/public/validation_processes/" + id + "/address", body)
      .pipe(tap((_) => console.log("PersonsService.postAddressValidationProcess: Ok.")));
  }

  public postFinishOnlineRegistration(id) {
    return this.publicHttp
      .post("/api/persons/" + id + "/finish_online_registration", null)
      .pipe(tap((_) => console.log("PersonsService.postFinishOnlineRegistration: Ok.")));
  }

  public getPersonBySlug(slug): Observable<any> {
    let options = {
      params: { slug },
    };
    return this.publicHttp
      .get("/api/public/persons/sponsor", options)
      .pipe(tap((_) => console.log("PersonsService.getPersonBySlug: Ok.")));
  }

  public getPostalCode(postal_code: string, closest: boolean = false): Observable<any> {
    let options = {
      params: { closest: closest.toString() },
    };
    return this.publicHttp
      .get("/api/public/postal_codes/" + postal_code, options)
      .pipe(tap((_) => console.log("PersonsService.getPostalCode: Ok.")));
  }

  public getLastQuote(person_id): Observable<any> {
    return this.publicHttp
      .get("/api/public/persons/" + person_id + "/quotes/last")
      .pipe(tap((_) => console.log("PersonsService.getLastQuote: Ok.")));
  }

  public findPersonByEmail(email: string): Observable<any> {
    const options = {
      params: { email },
    };
    return this.publicHttp
      .get("/api/persons", options)
      .pipe(tap((_) => console.log("PublicPersonsService.findPersonByEmail: Ok.")));
  }

  public update({
    first_name = undefined,
    middle_name = undefined,
    first_last_name = undefined,
    second_last_name = undefined,
    rfc = undefined,
    fecha_nacimiento = undefined,
    regime = undefined,
    phones = undefined,
    emails = undefined,
    relation = undefined,
    ingresos_comprobables = undefined,
    numero_telefono = undefined,
    expenses = undefined,
    person_id,
  }): Observable<any> {
    const params = {
      primer_nombre: first_name,
      segundo_nombre: middle_name,
      primer_apellido: first_last_name,
      segundo_apellido: second_last_name,
      rfc,
      fecha_nacimiento,
      phones,
      emails,
      relation,
      regime,
      ingresos_comprobables,
      numero_telefono,
      expenses,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] == undefined) {
        delete params[key];
      }
    });
    return this.publicHttp
      .put("/api/persons/" + person_id, params)
      .pipe(tap((_) => console.log("PublicPersonsService.update: Ok.")));
  }

  public nextCall(id, activity_id): Observable<any> {
    const options = { params: { activity_id } };
    return this.publicHttp
      .get(`/api/persons/${id}/next_call`, options)
      .pipe(tap((_) => console.log("PublicPersonsService.nextCall: Ok.")));
  }

  public putSolicitud(id, quote): Observable<any> {
    return this.publicHttp
      .put(`/api/persons/${id}/quotes/update_last`, quote)
      .pipe(tap((_) => console.log("PublicPersonsService.putSolicitud: Ok.")));
  }

  public saveBuroWithNip(params: {
    person_id;
    primer_nombre;
    segundo_nombre;
    primer_apellido;
    segundo_apellido;
    fecha_nacimiento;
    rfc;
    calle;
    numero;
    interior;
    colonia;
    municipio_delegacion;
    estado;
    codigo_postal;
  }): Observable<any> {
    return this.publicHttp
      .post(`/api/persons/${params.person_id}/consultas_buros/create_validation_nip`, params)
      .pipe(tap((_) => console.log("PublicPersonsService.saveBuroWithNip: Ok.")));
  }

  public validateNip({ consulta_buro_id, nip }): Observable<any> {
    return this.publicHttp
      .post(`/api/consultas_buros/${consulta_buro_id}/validate_nip`, { nip })
      .pipe(tap((_) => console.log("PublicPersonsService.validateNip: Ok.")));
  }

  public sendNotificationAuthorizedClient(person_id, user_id): Observable<any> {
    const params = {
      user_id,
    };
    return this.publicHttp
      .post(`/api/persons/${person_id}/consultas_buros/notification_nip_success`, params)
      .pipe(tap((_) => console.log("PublicPersonsService.sendNotificationAuthorizedClient: Ok.")));
  }

  public getBuro(person_id, user_id): Observable<any> {
    const options = {
      params: {
        usuario_id: user_id,
      },
    };
    return this.publicHttp
      .get(`/api/persons/${person_id}/consultas_buros/datos_buro`, options)
      .pipe(tap((_) => console.log("PublicPersonsService.getBuro: Ok.")));
  }

  public getEmail(email, contactable_type): Observable<any> {
    const options = {
      params: {
        email,
        "contactable_types[]": contactable_type,
      },
    };
    return this.publicHttp
      .get("/api/email", options)
      .pipe(tap((_) => console.log("PublicPersonsService.getEmail: Ok.")));
  }

  public create(person: any, sponsorId?: number | string | null, owner?: string): Observable<any> {
    const params = Object.assign({}, person, { sponsor_id: sponsorId, owner });
    return this.publicHttp
      .post("/api/persons", params)
      .pipe(tap((_) => console.log("PublicPersonsService.create: Ok.")));
  }

  public addBuro(
    id,
    first_name,
    middle_name,
    first_last_name,
    second_last_name,
    rfc,
    fecha_nacimiento,
    calle,
    numero,
    codigo_postal,
    colonia,
    tiene_credito_automotriz,
    tiene_hipoteca,
    tiene_tarjeta_credito,
    municipio_delegacion,
    ultimos_digitos_tarjeta_credito,
    estado,
    ciudad
  ): Observable<any> {
    return this.getPostalCode(codigo_postal, true).pipe(
      map((postalCode) => postalCode.codigo_postal),
      switchMap((postalCode) => {
        const params = {
          id,
          primer_nombre: first_name,
          segundo_nombre: middle_name,
          primer_apellido: first_last_name,
          segundo_apellido: second_last_name,
          rfc,
          fecha_nacimiento,
          calle,
          numero,
          codigo_postal: postalCode.toString().padStart(5, "0"),
          colonia,
          tiene_credito_automotriz,
          tiene_hipoteca,
          tiene_tarjeta_credito,
          municipio_delegacion,
          ultimos_digitos_tarjeta_credito,
          estado,
          ciudad,
        };
        return this.publicHttp
          .post(`/api/persons/${id}/consultas_buros`, params)
          .pipe(tap((_) => console.log("PublicPersonsService.addBuro: Ok.")));
      })
    );
  }

  public resendNip(person_id, nip, user_id, emails: any[], phone_numbers: any[], person_titular_id): Observable<any> {
    const params = {
      nip,
      user_id,
      emails,
      phone_numbers,
      person_titular_id,
    };
    return this.publicHttp
      .post(`/api/persons/${person_id}/consultas_buros/resend_nip`, params)
      .pipe(tap((_) => console.log("PublicPersonsService.resendNip: Ok.")));
  }
}
